import { useApolloClient } from '@apollo/client/react';
import {
    type ConnectorSDKCallbackMetadata,
    ConnectorSDKEventType,
} from '@quiltt/core';
import { useFeatureGate } from '@statsig/expo-bindings';
import { useQueryClient } from '@tanstack/react-query';
import { router } from 'expo-router';
import { useAtom, useSetAtom } from 'jotai';
import { useCallback } from 'react';
import { Image, Platform } from 'react-native';
import { useToastController, View } from 'smoothie';

import { useAuthedUser } from '../../common/providers/AuthedUserProvider';
import { FeatureFlags } from '../../feature-flags/FeatureFlags';
import LogRocketFacade from '../../logger/log-rocket/LogRocketFacade';
import { captureException, captureMessage } from '../../logger/Logger';
import { useWatchQuilttConnectionStatus } from '../providers/QuilttConnectionStatusProvider';
import {
    isQuilttConnectorOpenAtom,
    onQuilttConnectorExitAtom,
    selectedConnectionIdAtom,
} from '../state/HolisticAtoms';
import {
    BalancesNotReadyStatuses,
    GET_CONNECTION_QUERY,
    type QuilttConnectionResponse,
    type QuilttConnectorSource,
} from '../types/Quiltt';
import useGetHolisticSummary from './useGetHolisticSummary';

const useQuilttConnectorCallbacks = (context?: QuilttConnectorSource) => {
    const queryClient = useQueryClient();
    const user = useAuthedUser();
    const toast = useToastController();
    const { refetch: refetchHolisticSummary } = useGetHolisticSummary(false);

    const setIsQuilttConnectorOpen = useSetAtom(isQuilttConnectorOpenAtom);
    const [onQuilttConnectorExit, setOnQuilttConnectorExit] = useAtom(
        onQuilttConnectorExitAtom,
    );
    const setSelectedConnectionId = useSetAtom(selectedConnectionIdAtom);

    const { value: enablePostAccountLinkingScreen } = useFeatureGate(
        FeatureFlags.ENABLE_POST_ACCOUNT_LINKING_SCREEN,
    );

    const watchQuilttConnectionStatus = useWatchQuilttConnectionStatus();
    const client = useApolloClient();

    const onEvent = useCallback(
        (
            eventType: ConnectorSDKEventType,
            metadata: ConnectorSDKCallbackMetadata,
        ) => {
            if (
                [
                    ConnectorSDKEventType.Open,
                    ConnectorSDKEventType.Load,
                ].includes(eventType)
            ) {
                setIsQuilttConnectorOpen(true);
            }
            captureMessage(
                `Quiltt connection event: ${JSON.stringify({
                    event_type: eventType,
                    ...metadata,
                    fruitful_user_id: user.id,
                })}`,
            );
        },
        [],
    );

    const onExit = useCallback(() => {
        setIsQuilttConnectorOpen(false);
        LogRocketFacade.log('Done connecting, resuming view capture');
        LogRocketFacade.unpauseViewCapture();

        if (onQuilttConnectorExit != null) {
            onQuilttConnectorExit?.();
            setOnQuilttConnectorExit(null);
        } else {
            if (Platform.OS !== 'web') {
                router.back();
            }
        }
    }, []);

    const onExitError = useCallback(() => {
        captureException(new Error('Quiltt connection error'), {
            userId: user.id,
        });
        toast.show('Error connecting institution');
    }, []);

    const onExitSuccess = useCallback(
        async (metadata: ConnectorSDKCallbackMetadata) => {
            if (metadata.connectionId != null) {
                const { data } = await client.query<QuilttConnectionResponse>({
                    query: GET_CONNECTION_QUERY,
                    variables: { connectionId: metadata.connectionId },
                });

                const connection = data?.connection ?? {};
                const { institution, status, id } = connection;

                toast.show(`${institution.name} connected`, {
                    icon: institution.logo?.url != null && (
                        <View
                            padding={12}
                            backgroundColor="$gray100"
                            borderRadius={8}
                        >
                            <Image
                                source={{ uri: institution.logo.url }}
                                width={24}
                                height={24}
                            />
                        </View>
                    ),
                });

                if (BalancesNotReadyStatuses.includes(status)) {
                    watchQuilttConnectionStatus(id);
                }

                // bust the tanstack cache so we can fetch new accounts
                await queryClient.invalidateQueries({
                    queryKey: [`getAccountOverviewSummary/${user.id}`],
                    exact: true,
                });

                if (context === 'onboarding') {
                    void refetchHolisticSummary();
                    router.navigate('/onboarding/connect-accounts');
                } else if (context === 'profile') {
                    await refetchHolisticSummary();
                    router.replace('/profile/external-accounts');
                } else if (enablePostAccountLinkingScreen) {
                    void refetchHolisticSummary();
                    setSelectedConnectionId(id);
                    router.navigate('/holistic/connect-institution-success');
                } else {
                    await refetchHolisticSummary();
                }
            }
        },
        [context],
    );

    return {
        onEvent,
        onExit,
        onExitError,
        onExitSuccess,
    };
};

export default useQuilttConnectorCallbacks;
